<template>
  <div
    class="help-header"
    :class="{ 'help-header--products-expanded': isProductsExpanded }"
    :style="shareFixedTopStyle"
  >
    <div class="help-header__left">
      <h1 ref="titleRef" class="help-header__title">{{ title }}</h1>
      <div v-if="$slots.subtitle || applicableProducts.length > 0" class="help-header__bottom">
        <slot name="subtitle">
          <div class="help-header__products">
            <BaseExpandableText
              :line-clamp="PRODUCT_LINE_CLAMP"
              :line-height="PRODUCT_LINE_HEIGHT"
              @change="handleProductsExpandChange"
            >
              <span class="help-header__products-label">适用产品：</span>
              <span class="help-header__products-text">{{ applicableProducts.join('，') }}</span>
            </BaseExpandableText>
          </div>
        </slot>
      </div>
    </div>
    <SharePopup
      v-if="showShare"
      class="help-header__share"
      :share-url="shareUrl"
      :share-title="title"
      :share-link-label="shareLinkLabel"
      @copy="handleShareCopy"
      @save="handleShareSave"
    >
      <template #trigger>
        <RoundOutlineButton type="default" size="large" class="help-header__share-button">
          <template #icon>
            <icon-share-internal />
          </template>
          分享
        </RoundOutlineButton>
      </template>
    </SharePopup>
  </div>
</template>

<script setup>
/**
 * @description 帮助详情页标题和分享组件
 * @param {string} title - 文章标题
 * @param {Array<string>} applicableProducts - 适用产品列表
 * @emits share - 分享按钮点击事件
 */

import { IconShareInternal } from '@arco-design/web-vue/es/icon'
import RoundOutlineButton from '~/components/base/button/RoundOutlineButton.vue'
import SharePopup from '~/components/base/SharePopup.vue'

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  applicableProducts: {
    type: Array,
    default: () => [],
  },
  showShare: {
    type: Boolean,
    default: false,
  },
  shareUrl: {
    type: String,
    default: '',
  },
  shareLinkLabel: {
    type: String,
    default: '内容详情链接',
  },
})

const emit = defineEmits(['share'])

const PRODUCT_LINE_CLAMP = 2
const PRODUCT_LINE_HEIGHT = 22
const SHARE_BUTTON_HEIGHT = 40

const titleRef = ref(null)
const isProductsExpanded = ref(false)
const fixedShareTop = ref(null)

const shareFixedTopStyle = computed(() => {
  if (!isProductsExpanded.value || fixedShareTop.value === null) return {}
  return { '--share-fixed-top': `${fixedShareTop.value}px` }
})

const updateFixedShareTop = () => {
  if (!import.meta.client || !titleRef.value) return
  const titleStyle = window.getComputedStyle(titleRef.value)
  const titleMarginBottom = Number.parseFloat(titleStyle.marginBottom) || 0
  fixedShareTop.value =
    titleRef.value.offsetHeight +
    titleMarginBottom +
    PRODUCT_LINE_CLAMP * PRODUCT_LINE_HEIGHT -
    SHARE_BUTTON_HEIGHT
}

const handleProductsExpandChange = (expanded) => {
  updateFixedShareTop()
  isProductsExpanded.value = expanded
}

onMounted(() => {
  updateFixedShareTop()
  if (import.meta.client) {
    window.addEventListener('resize', updateFixedShareTop)
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    window.removeEventListener('resize', updateFixedShareTop)
  }
})

/**
 * @description 处理分享链接复制
 */
const handleShareCopy = () => {
  emit('share')
}

/**
 * @description 处理二维码保存
 */
const handleShareSave = () => {
  emit('share')
}
</script>

<style lang="scss" scoped>
.help-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  position: relative;
  margin-bottom: 0;
  padding-bottom: 24px;
  border-bottom: 1px solid $border-dashed-color;

  @include respond-to-max('lg') {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 0;
    padding-bottom: 16px;
  }
}

.help-header__left {
  flex: 1;
  min-width: 0;

  @include respond-to-max('lg') {
    width: 100%;
  }
}

.help-header__bottom {
  width: 100%;
}

.help-header__title {
  font-size: $font-size-display-1;
  font-weight: 600;
  color: $text-title-color;
  margin: 0 0 16px 0;
  line-height: $line-height-display-1;
  // 详情标题允许换行，但不折叠原文中的连续空格。
  white-space: pre-wrap;

  @include respond-to-max('lg') {
    font-size: $font-size-title-2;
    line-height: $line-height-title-2;
    margin-bottom: 8px;
  }
}

.help-header__products {
  font-size: 14px;
  color: $text-subtitle-color;
  line-height: 22px;
  width: 100%;
  min-width: 0;

  @include respond-to-max('lg') {
    font-size: 14px;
    line-height: 22px;
    width: 100%;
  }
}

.help-header:not(.help-header--products-expanded) .help-header__products {
  max-height: 44px;
  overflow: hidden;
}

.help-header__products-label {
  margin-right: 4px;
  color: $text-subtitle-color;
}

.help-header__products-text {
  display: inline;
}

.help-header__share {
  flex-shrink: 0;

  @include respond-to-max('lg') {
    align-self: flex-start;
  }
}

.help-header__share-button {
  width: 82px;

  @include respond-to-max('lg') {
    width: 82px;
  }
}

.help-header--products-expanded {
  align-items: flex-start;

  .help-header__left {
    width: 100%;
    padding-right: 106px;
    box-sizing: border-box;
  }

  .help-header__share {
    position: absolute;
    right: 0;
    top: var(--share-fixed-top, 60px);
  }
}

@include respond-to-max('lg') {
  .help-header--products-expanded {
    .help-header__left {
      padding-right: 0;
    }

    .help-header__share {
      position: static;
    }
  }
}
</style>
